import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Globe, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTimeZonesList } from '@/lib/utils';

interface TimezoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  disabled?: boolean;
  className?: string;
}

export const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className,
}) => {
  const [open, setOpen] = useState(false);

  const timezoneOptions = getTimeZonesList().map((tz) => ({
    value: tz.zone,
    label: `${tz.text} (${tz.zone})`,
  }));

  return (
    <div className={cn('flex items-center gap-4 p-4 bg-slate-50 rounded-xl', className)}>
      <Globe className="h-5 w-5 text-slate-500" />
      <div className="flex-1">
        <Label className="text-sm font-medium">Timezone</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between mt-1"
              disabled={disabled}
            >
              {value
                ? timezoneOptions.find((tz) => tz.value === value)?.label || value
                : 'Select timezone...'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder="Search timezone..." />
              <CommandList>
                <CommandEmpty>No timezone found.</CommandEmpty>
                <CommandGroup className="max-h-64 overflow-auto">
                  {timezoneOptions.map((tz) => (
                    <CommandItem
                      key={tz.value}
                      value={tz.label}
                      onSelect={() => {
                        onChange(tz.value);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === tz.value ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {tz.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

